// "Find by Features" / brand-model comparison document script. This is the
// base document script for EVERY export type: the generator's run() calls
// run_bij unconditionally regardless of the submission's type (its
// run_scanners method has no callers), so bij, latin-bij, p-series,
// t-series AND scanners exports all ship this script when its attach
// conditions hold (see ./hydrate.js).
//
// This is PDF-side JavaScript: it runs inside Acrobat, not in the browser.
// It is a port of the pdf-generator jar's scripts_bij.js resource; it lives
// here so it can be changed without rebuilding and redeploying the generator.
//
// The `{...}` tokens below are NOT JavaScript template placeholders. The
// frontend hydrates every token it knows (see ./hydrate.js):
// {annotationPageIndices}, {mappedModelsForBrandAndModel}, {mappedHideLogic},
// {productFilters}, {partialMatches}, {color_primaryMatch},
// {color_secondaryMatch}, {highlightHeight}, {color_visibility}.
// {pageCounts} and {totalAppendedPdfPages} MUST stay verbatim: only the
// generator knows the appended promo PDFs' page counts, so it substitutes
// those two server-side. ({email}/{subject}/{body}-style placeholders do not
// occur here, but see quick-compare.js.) String.raw keeps the text
// byte-for-byte as written -- no escape processing, no interpolation.
//
// PERFORMANCE (task 2026-08-31_find-by-features-performance): a feature
// dropdown pick used to take ~5 s because the visibility-toggle branch wrote
// annot.hidden unconditionally to every visibility annotation and Acrobat
// redraws an annotation synchronously on EVERY property write. This version
// keeps the same visual and behavioral semantics, with each change commented
// with the Acrobat-runtime mechanism it exploits:
//
// 1. Acrobat redraws an annotation synchronously on EVERY property write,
//    whether or not the value changed. Fix: skip writes whose target state
//    equals the current state (setAnnotVisibility already had exactly this
//    guard -- the plain branch just didn't use it).
// 2. Doc.delay = true queues all appearance redraws until it is set back to
//    false, at which point one combined redraw happens (documented in the
//    Acrobat JS API for fields; community-established for annotation
//    batches). Fix: wrap every multi-annotation write phase in
//    docRef.delay, in try/finally so an exception can never leave the
//    document in delayed state.
// 3. Every field-value write reruns the Calculate script of EVERY listener
//    field in the document. brandModified performs two field writes
//    (setItems + value) and clearFiltersClicked performs one per filter, so
//    each triggered that many full chain passes. Fix: suspend Doc.calculate
//    around the writes and run one calculateNow() at the end -- one chain
//    pass instead of N. The suppressed nested passes were no-ops (every
//    listener guards on value-unchanged), so semantics are identical.
// 4. getField is called per interaction for names that never change. Fix:
//    resolve once and cache (standard Acrobat practice; Field objects stay
//    valid for the life of the document).
//
// For per-phase timings in the Acrobat console (Ctrl+J), swap the import in
// ./hydrate.js to the unwired instrumented variant:
//   import bijScripts from "./scripts-bij-instrumented";
// (that variant carries the pre-optimization write pattern, timestamped).
//
// SAVE-THEN-REOPEN (owner report 2026-09-01): a saved PDF reopens with the
// user's dropdown selections and this script's helper annotations persisted,
// while every script variable is back at its initial value -- reopening is a
// full state desync unless the open code derives its state from the document
// instead of assuming the pristine export baseline. This version makes the
// open sequence idempotent:
//
// R1. setupToggleableAnnotations no longer concat-pollutes
//     visibilityAnnotations with every page annotation (Links included) when
//     the annotations already exist (the old code doubled the list and ended
//     every reopened document with dead filters).
// R2. Saved helpers are adopted into their product slot by the index carried
//     in their NAME, not by list position: the Acrobat JS API guarantees no
//     getAnnots order, and mappedHideLogic references products by 1-based
//     display index, so positional adoption silently toggles the wrong
//     products. (The differential suite proves name order == product order
//     on fresh documents, so the name index IS the product slot.)
// R3. Primary/secondary highlight helpers get their own name prefixes; they
//     used to share the visibility prefix, which made a reopened
//     partial-match document adopt all three helpers of a product into the
//     disabled list and throw on the first pick. Legacy trios saved by older
//     script versions are told apart by opacity and fill color.
// R4. On a reopened document applyListener keeps the user's saved dropdown
//     values (fresh exports still get primed to their header item exactly as
//     before) and the open sequence ends by deriving the annotation states
//     from the field values once -- the document reopens showing the same
//     visible set it was saved with, and documents saved by older script
//     versions in a broken annotation state self-heal on open.
// R5. Doc.syncAnnotScan() before the adopt-or-create decision: Acrobat loads
//     a saved document's annotations lazily, so getAnnots() returned null at
//     open-action time and a FRESH helper set was stacked on top of the
//     saved one every save/reopen cycle -- the saved overlays froze in their
//     saved states forever (nothing referenced them, so no interaction could
//     clear them: the owner-reported symptom).
//
// STATIC AUDIT (2026-09-01, same task): latent-bug sweep of the PDF-side
// scripts, each fix reproduced in the mock runtime before changing:
//
// A1. "Annotations present" no longer implies "reopened document": a page can
//     carry annotations that are not this script's helpers (a user's comment
//     saved from a viewer that never ran or never persisted the script's
//     helpers, or a viewer whose getAnnots reports an empty page as [] rather
//     than null). The adopt branch used to claim the page anyway, so the
//     helper set was never created and the filters were dead. Now the page
//     counts as reopened only when at least one helper was actually adopted;
//     otherwise the fresh-creation branch runs.
// A2. clearFiltersClicked tolerates a missing filter field. The sanitizer
//     (frontend ./sanitize.js) detaches filter fields whose hide logic is
//     unusable -- the clear button still exists in such an export, and the
//     old unguarded getField(...).value=... threw on the first click.
// A3. hideableControllerValueChanged tolerates a filter field that has no
//     entry in mappedHideLogic (counts it as unselected instead of throwing).
//     Reachable when the export ships filter dropdowns without hide-logic
//     data -- mappedHideLogic then hydrates to [] and the old unconditional
//     mappedHideLogic[f.name][f.value] threw on every pick.
// A4. brandModified captures the model dropdown's RESET item from
//     getItemAt(0) instead of the current valueAsString. On a reopened
//     document the current value is the user's saved model (R4 keeps it), so
//     the first brand switch used to offer the stale model name as the
//     dropdown's header slot -- and never "Choose a Model". On fresh
//     documents item 0 IS the committed header value (probed: every dropdown
//     ships /V = its first item), so the capture is byte-identical there.

const scripts = String.raw`
// OPT(4): document reference captured once — the open action runs with the
// Doc as "this"; handlers can then reach Doc.delay/Doc.calculate without
// depending on call-site "this".
var docRef = this;
// OPT(4): field cache. Fields never appear or disappear in these exports,
// so resolve each name once. Falls back to getField transparently.
var fieldCache = {};
function gF(n) {
	if (fieldCache[n] === undefined)
		fieldCache[n] = this.getField(n);
	return fieldCache[n];
}
function rand(n) { return Math.floor(Math.random() * n) % n; }

// OPT(2)+(3): run fn with appearance redraws queued (Doc.delay) and, when
// suspendCalc is true, the Calculate chain suspended (Doc.calculate).
// try/finally guarantees restoration even if fn throws. recalcAfter runs
// one calculateNow() afterwards, replaying the single chain pass the
// suppressed writes still need — pass it ONLY from handlers that run
// outside the Calculate chain (button Mouse Up); handlers already running
// inside a Calculate pass must not re-enter calculation, and the nested
// passes they suppress are all guarded no-ops anyway.
function batchedUpdate(fn, suspendCalc, recalcAfter) {
	var prevDelay = docRef.delay;
	var prevCalc = docRef.calculate;
	docRef.delay = true;
	if (suspendCalc) docRef.calculate = false;
	try {
		fn();
	} finally {
		if (suspendCalc) docRef.calculate = prevCalc;
		docRef.delay = prevDelay;
	}
	if (recalcAfter && prevCalc) docRef.calculateNow();
}

var modelValue = '';
var modelChangeLock = 0;
function brandModified(brandField) {
	var modelField = gF('model');
	if (!modelField) return;
	if (modelChangeLock == 0) {
	    // AUDIT(A4): the reset item is the dropdown's FIRST ITEM, not its
	    // current value -- on a reopened document the current value is the
	    // user's saved model, which would become the permanent header slot.
	    modelValue = modelField.numItems != 0 ? modelField.getItemAt(0) : modelField.valueAsString;
	    modelChangeLock = 1;
	}
	var brandValue = brandField.valueAsString;

	// clear modelField when brand value is the first value ("Choose a Brand")
	if (brandValue == brandField.getItemAt(0)) {
		// OPT(3): setItems + value are two field writes; each reran the whole
		// Calculate chain (12 listener scripts) mid-handler. All those nested
		// passes exit at their value-unchanged guards, so suspending the chain
		// is behavior-preserving; no replay — we are already inside a
		// Calculate pass.
		batchedUpdate(function() {
			modelField.setItems(['']);
			modelField.value = '';
		}, true, false);
		return;
	}
	// set new options on modelField
	var options = getOptionsMap(brandValue);
	if (!options) return;
	var names = Object.keys(options);
	// prepend all entries
    names = [modelValue].concat(names);
	// OPT(3): same mechanism as above for the setItems write.
	batchedUpdate(function() {
		modelField.setItems(names);
	}, true, false);
}
function modelModified(modelField, doc) {
	var brandField = gF('brand');

	if (!brandField || !brandField.value) return;
	var options = getOptionsMap(brandField.value);
	if (!options) return;
	var s = '';
	for (var k in options)
		s += k + '; ';

	// jump to page if valid index
	var pageIndex = options[modelField.value] + 1;
	brandModelPageToJumpTo = pageIndex;
}

// map field names to actions
var fieldConfig = {
	brand: {
		isSelect: true,
		action: brandModified
	},
	model: {
		isSelect: true,
		action: modelModified
	}
};


var prevValues = {};
function fieldEdited(name) {
  var f = gF(name);
  if (!f) return;
  // keep track of previous value - do nothing if previous value is current
  if (prevValues[name] == f.value || !f.value) return;
  prevValues[name] = f.value;

  if (fieldConfig[name] && fieldConfig[name].action)
    fieldConfig[name].action(f, this);
}

function applyListener(fieldName, isSelect) {
	var field = gF(fieldName);
	if (!field) return;
	if (isSelect && field.numItems != 0) {
	  // REOPEN(R4): a fresh export gets its dropdown primed to the header
	  // item, exactly as always. A saved-then-reopened document's value IS
	  // the user's saved selection -- keep it, and prime the change tracker
	  // instead so the first Calculate pass after reopening does not re-fire
	  // every handler against an unchanged value.
	  if (!reopenedDocument)
	    field.value = field.getItemAt(0);
	  else
	    prevValues[fieldName] = field.value;
	}

	field.setAction('Calculate', 'fieldEdited(\"' + field.name + '\")');
	field.commitOnSelChange = true;
}
function getPageLinks(idx) {
	var b = this.getPageBox("Crop", idx);
	// skip "back to top" links
	b[1] -= 30;
	return this.getLinks(idx, b);
}

var rgb_max = 255;
function hex2rgb(hex) {
  var rgb_chunks = hex.substring(1).match(/([a-f\d]{2})/gi) || [];
  if (rgb_chunks.length != 3)
    return [1, 1, 1];

  return rgb_chunks.map(function(chunk) {
    return parseInt(chunk, 16) / rgb_max;
  });
}

// add annotations
var visibilityAnnotations = [];
var primaryAnnotations = [];
var secondaryAnnotations = [];
// REOPEN: true when the helper annotations already exist, i.e. the PDF was
// saved and reopened -- open-time setup then derives its state from the
// document instead of assuming the pristine export baseline
var reopenedDocument = false;

var annotPadding = 1;
var annotationPageIndices = [{annotationPageIndices}];
var highlightHeight = {highlightHeight};
var partialMatches = {partialMatches};
var base_color_visibility = "{color_visibility}";
var color_primaryMatch = ['RGB', ...hex2rgb("{color_primaryMatch}")];
var color_secondaryMatch = ['RGB', ...hex2rgb("{color_secondaryMatch}")];
var color_visibility = ['RGB', ...hex2rgb(base_color_visibility)];

var annotPageLinks = {};
annotationPageIndices.forEach(function(pageIndex) {
  annotPageLinks[pageIndex] = sortByXAndYPos(getPageLinks(pageIndex), false, 10);
});

function setAnnotColor(annot, color) {
  annot.fillColor = color;
  annot.strokeColor = color;
}

var annotDefaultAuthor = 'Bitbot';
var annotDefaultShape = 'Square';


function createVisibilityAnnot(page, author, name, type, rect, opacity, color) {
  return this.addAnnot({
    page: page,
    author: author,
    name: name,
    type: type,
    rect: rect,
    opacity: opacity,
    fillColor: color,
    strokeColor: color,
    lock: true,
    hidden: true
  });
}
var visibility_transparent = 0.8;
var visibility_opaque = 1;
function createVisibilityAnnot_disabled(pageIndex, pageLink, annotIndex) {
  var r = pageLink.rect;
  var annotRect = [r[0] - annotPadding, r[3] - annotPadding - 1, r[2] + annotPadding, r[1] + annotPadding];
  return createVisibilityAnnot(
    pageIndex,
    annotDefaultAuthor,
    pfx_annot_visibility + annotIndex,
    annotDefaultShape,
    annotRect,
    visibility_transparent,
    color_visibility
  );
}

function createPartialVisibilityAnnot(pageIndex, pageLink, annotIndex, isPrimary) {
  var r = pageLink.rect;
  var color = isPrimary ? color_primaryMatch : color_secondaryMatch;
  // REOPEN(R3): each helper role gets its own name prefix -- all three
  // helpers of a product used to share the visibility prefix, which made
  // them indistinguishable by name after a save/reopen (legacy trios are
  // told apart at adoption by opacity and fill color)
  var namePrefix = isPrimary ? pfx_annot_primary : pfx_annot_secondary;
  var annotRect = [r[0] + 1, r[1] - highlightHeight, r[2] - 1, r[1] - 1];
  return createVisibilityAnnot(
    pageIndex,
    annotDefaultAuthor,
    namePrefix + annotIndex,
    annotDefaultShape,
    annotRect,
    visibility_opaque,
    color
  );
}

function createVisibilityAnnot_primary(pageIndex, pageLink, annotIndex) {
  return createPartialVisibilityAnnot(pageIndex, pageLink, annotIndex, true);
}

function createVisibilityAnnot_secondary(pageIndex, pageLink, annotIndex) {
  return createPartialVisibilityAnnot(pageIndex, pageLink, annotIndex, false);
}

var pfx_annot_visibility = 'VisibilityHelper_Disabled_';
var pfx_annot_primary = 'VisibilityHelper_Primary_';
var pfx_annot_secondary = 'VisibilityHelper_Secondary_';

// REOPEN(R2): place a saved helper at the product slot its name carries.
// getAnnots gives NO order guarantee, and the three arrays are indexed by
// product slot (mappedHideLogic references products by 1-based display
// index), so positional adoption would silently toggle the wrong products.
// Non-numeric suffixes are skipped: quick-compare names its overlays with
// the visibility prefix plus a field name, and users can add their own
// comments to the page. Returns true when the annotation was adopted as a
// helper -- AUDIT(A1): the caller decides adopt-vs-create from that, not
// from the mere presence of page annotations.
function adoptSavedAnnotation(pageAnnot) {
	var annotName = typeof pageAnnot.name == 'string' ? pageAnnot.name : '';
	var targetArray = null;
	var namePrefix = null;
	if (annotName.indexOf(pfx_annot_primary) > -1) {
		targetArray = primaryAnnotations;
		namePrefix = pfx_annot_primary;
	} else if (annotName.indexOf(pfx_annot_secondary) > -1) {
		targetArray = secondaryAnnotations;
		namePrefix = pfx_annot_secondary;
	} else if (annotName.indexOf(pfx_annot_visibility) > -1) {
		targetArray = visibilityAnnotations;
		namePrefix = pfx_annot_visibility;
	}
	if (!targetArray) return false;
	var slot = parseInt(annotName.substring(annotName.indexOf(namePrefix) + namePrefix.length), 10);
	if (isNaN(slot) || slot < 0) return false;
	if (targetArray === visibilityAnnotations && partialMatches)
		adoptLegacyPartialHelper(pageAnnot, slot);
	else
		targetArray[slot] = pageAnnot;
	return true;
}

// REOPEN(R3): documents saved before primary/secondary helpers had their own
// name prefixes named ALL THREE helpers of a product with the visibility
// prefix. The transparent overlay is the disabled helper; the two opaque
// highlight strips are told apart by fill color (primary wins a tie, which
// only happens when both highlight colors are configured equal).
function adoptLegacyPartialHelper(annot, slot) {
	if (annot.opacity != visibility_opaque) {
		visibilityAnnotations[slot] = annot;
		return;
	}
	var isPrimary = colorDistance(annot.fillColor, color_primaryMatch) <=
		colorDistance(annot.fillColor, color_secondaryMatch);
	if (isPrimary && primaryAnnotations[slot] === undefined)
		primaryAnnotations[slot] = annot;
	else if (secondaryAnnotations[slot] === undefined)
		secondaryAnnotations[slot] = annot;
	else
		primaryAnnotations[slot] = annot;
}

function colorDistance(a, b) {
	if (!a || !b) return 1e9;
	var distance = 0;
	for (var i = 1; i < 4; ++i)
		distance += Math.abs((Number(a[i]) || 0) - (Number(b[i]) || 0));
	return distance;
}

function setupToggleableAnnotations() {
	// REOPEN(R5): Acrobat loads a saved document's annotations LAZILY -- at
	// open-action time getAnnots() returns null for pages that have not been
	// scanned yet. Without forcing the scan, a reopened document looks
	// annotation-free here, so a FRESH helper set gets stacked on top of the
	// saved one: the saved overlays keep their saved states forever (no array
	// references them, so no interaction can ever clear them) while the new
	// set toggles invisibly underneath. syncAnnotScan() guarantees every
	// saved annotation is visible to getAnnots before the adopt-or-create
	// decision. (Guarded: third-party viewers may not implement it, and they
	// are also the viewers that never persisted the helpers to begin with.)
	if (typeof this.syncAnnotScan == 'function') this.syncAnnotScan();
	for (var i = 0; i < annotationPageIndices.length; ++i) {
		var pageIndex = annotationPageIndices[i];
		var pageAnnots = this.getAnnots(pageIndex);
		// prevent re-creating annotations (the PDF was saved and reopened).
		// REOPEN(R1): adopt every helper into its proper array and nothing
		// else -- the old code also ran "visibilityAnnotations =
		// visibilityAnnotations.concat(pageAnnots)", which appended every page
		// annotation again (Link annots included), so each helper was listed
		// twice, the duplicated tail indexed past the match-counter array, and
		// every annotation ended hidden=true: the filters looked dead on any
		// saved-then-reopened PDF (and every interaction paid double the
		// writes).
		if (pageAnnots != null) {
		  // AUDIT(A1): only an actually-adopted helper proves this is a
		  // saved-then-reopened export. A page whose annotations are all
		  // foreign (a user's comment persisted by a viewer that never ran
		  // this script, or a viewer reporting an empty page as []) must
		  // still get its helper set created, or the filters are dead.
		  var adoptedAnyHelper = false;
		  pageAnnots.forEach(function(pageAnnot) {
		    if (adoptSavedAnnotation(pageAnnot)) adoptedAnyHelper = true;
		  });
		  if (adoptedAnyHelper) {
		    reopenedDocument = true;
		    continue;
		  }
		}

		var links = annotPageLinks[pageIndex];

    // Create and hide annotations
		if (links == null || links.length == 0) return;
		for (var j = 0; j < links.length; ++j) {

			var annot_opacity = createVisibilityAnnot_disabled(pageIndex, links[j], j);
			visibilityAnnotations.push(annot_opacity);

			var items_to_hide = [annot_opacity];

			// Create additional annotations for partial matches
			if (partialMatches) {
			  var annot_primary_match = createVisibilityAnnot_primary(pageIndex, links[j], j);
			  var annot_secondary_match = createVisibilityAnnot_secondary(pageIndex, links[j], j);
        primaryAnnotations.push(annot_primary_match);
        secondaryAnnotations.push(annot_secondary_match);
        items_to_hide = [annot_opacity, annot_primary_match, annot_secondary_match];
      }

      items_to_hide.forEach(function(annot) {
			  annot.hidden = true;
      });
		}
	}
}

function sortByXAndYPos(elements, isAnnot, verticalPadding) {
	if (isAnnot === undefined)
		isAnnot = true;
	if (verticalPadding === undefined)
		verticalPadding = 0;

	// Annotations use index 3 for "upper left" while
	// Links and other elements use index "1"
	var yIdxInRect = isAnnot ? 3 : 1;

	return elements.sort(function(a,b) {
		var ax = a.rect[0];
		var ay = a.rect[yIdxInRect];
		var bx = b.rect[0];
		var by = b.rect[yIdxInRect];

		// within same approximately-Y position?
		if (Math.abs(ay - by + verticalPadding) < 20) {
			return ax > bx;
		}
		// not within approximately-same Y position
		else {
			return ay < by;
		}
	});
}

function sortVisibilityAnnotations() {
	// REOPEN(R2): adopted arrays are already slot-true (and may be sparse if
	// a helper was lost from an old save) -- a positional sort would break
	// the product mapping. Only fresh creations take the spatial sort.
	if (reopenedDocument) return;
	visibilityAnnotations = sortByXAndYPos(visibilityAnnotations);
}

//********************** Brands & Models **********************/
var minifiedBrandModelComboExportFromVueApp = {mappedModelsForBrandAndModel};
function getOptionsMap(brandValue) {
	var options = minifiedBrandModelComboExportFromVueApp[brandValue];
	if (!options) return null;
	var optMap = {};
	options.forEach(function(o) {
		optMap[o.name] = o.pageIndex;
	});
	return optMap;
}

function gotoPage(doc, idx) {
	var val = parseInt(idx);
	if (isNaN(val) || val < 0) return;
	doc.pageNum = val;
}
var brandModelPageToJumpTo = 0;

function viewComparisonClicked() {
  gotoPage(this, brandModelPageToJumpTo);
  brandModelPageToJumpTo = 0;
  resetBrandAndModelsFields();
}
function sortedLinks() {
  var firstPageLinks = getPageLinks(0);
  if (!firstPageLinks || !firstPageLinks[0]) return [];
  return sortByXAndYPos(firstPageLinks, false, 10);
}

function setViewComparisonFunctionality() {
  var firstPageLinks = sortedLinks();
  var viewComparisonLink = firstPageLinks[0];
  if (!firstPageLinks || firstPageLinks.length == 0)
    return;

  viewComparisonLink.setAction('viewComparisonClicked()');
}

//********************** Hide Logic (color, size, user, it, volume) **********************/
// ['color', 'size', 'user', 'it', 'volume']
var allHidableLogicFields = '{productFilters}'.split(',');

function clearFiltersClicked() {
  // OPT(3): each field-value reset reran the whole Calculate chain, and each
  // pass re-ran the annotation write loop for the filter it cleared —
  // clear-filters was ~6x the cost of a single dropdown pick. One suspended
  // batch plus one calculateNow() replay produces the identical final state
  // (this is a button Mouse Up handler, safely outside the Calculate chain).
  batchedUpdate(function() {
    allHidableLogicFields.forEach(function(logicField) {
      var field = gF(logicField);
      // AUDIT(A2): the sanitizer can detach filter fields (unusable hide
      // logic) while the clear button still exists -- skip, never throw
      if (!field) return;
      field.value = field.getItemAt(0);
    });
  }, true, true);
}

function setupDataFromServer() {
	allHidableLogicFields.forEach(function(logicField) {
		fieldConfig[logicField] = {
			isSelect: true,
			action: hideableControllerValueChanged,
		};
	});
}

function hideAnnots(annots) {
  annots.forEach(function(annot) {
    setAnnotVisibility(annot, true);
  });
}

function hideAllVisibilityAnnots() {
  // OPT(2): queue the redraws of however many of these guarded writes fire,
  // then repaint once.
  batchedUpdate(function() {
    hideAnnots(visibilityAnnotations);
    hideAnnots(primaryAnnotations);
    hideAnnots(secondaryAnnotations);
  }, false, false);
}

var mappedHideLogic = {mappedHideLogic};
// re-process *all* visibility field controllers any time a single one is changed
function hideableControllerValueChanged(changedField, doc) {
	// set all annotations as not visible
	var productFeatureMatchCtr = new Array(visibilityAnnotations.length);
	for (var i = 0; i < productFeatureMatchCtr.length; ++i)
		productFeatureMatchCtr[i] = 0;

	// verify all field values
	var empty_values_ctr = 0;
	for (var i = 0; i < allHidableLogicFields.length; ++i) {
		var f = gF(allHidableLogicFields[i]);
		// REOPEN(R4): also runs once directly at open on reopened documents --
		// tolerate a missing filter field instead of throwing
		if (!f || !f.value) {
		  ++empty_values_ctr;
		  continue;
		}

		// AUDIT(A3): an export can ship filter dropdowns without hide-logic
		// data (mappedHideLogic hydrates to []) -- count the field as
		// unselected instead of throwing on the dereference
		var hideLogicOptionsForField = mappedHideLogic[f.name];
		var annotationsToHide = hideLogicOptionsForField && hideLogicOptionsForField[f.value];
		if (!annotationsToHide) {
		  ++empty_values_ctr;
		  continue;
		}

		// set these annotations as visible (e.g. "disable" the product)
		for (var j = 0; j < annotationsToHide.length; ++j)
			++productFeatureMatchCtr[annotationsToHide[j]-1];
	}

  // Hide all annotations when all dropdowns have no selected value
  if (empty_values_ctr === allHidableLogicFields.length) {
    hideAllVisibilityAnnots();
    return;
  }

  // OPT(1)+(2): the write phase — the interaction's entire perceptible cost.
  // Guarded writes (setAnnotVisibility) skip annotations whose state is
  // already correct: the base plain branch wrote all of them unconditionally
  // and Acrobat redraws on every write even when the value is unchanged. The
  // writes that remain are queued under Doc.delay and repainted once.
  batchedUpdate(function() {
    if (partialMatches) {
      // Partial matches
      visibilityAnnotations.forEach(function(annot, idx) {
        var is_primary = !(productFeatureMatchCtr[idx] === 0);
        var is_disabled = !((productFeatureMatchCtr[idx] + empty_values_ctr) == allHidableLogicFields.length);
        // Primary
        setAnnotVisibility(primaryAnnotations[idx], is_primary);
        // Disabled
        setAnnotVisibility(visibilityAnnotations[idx], is_disabled);
        // Secondary
        setAnnotVisibility(secondaryAnnotations[idx], !is_primary || !is_disabled);
      });
    } else {
      // No partial matches
      visibilityAnnotations.forEach(function(annot, idx) {
        setAnnotVisibility(annot, productFeatureMatchCtr[idx] === 0);
      });
    }
  }, false, false);
}

// Helper to speedup toggling annotation visibility states.
// REOPEN(R3): a reopened document can be missing a helper (adoption is
// best-effort over what an older script version saved) -- skip, never throw.
function setAnnotVisibility(annot, hidden) {
  if (!annot || annot.hidden === hidden) return;
  annot.hidden = hidden;
}

//********************** Promotions **********************/
var appendedPdfPageCounts = {pageCounts};
var totalAppendedPdfPages = {totalAppendedPdfPages};

/********************** Back-to-top links**********************/
function resetBrandAndModelsFields() {
	// resetting "brand" also resets "models"
	var fieldToReset = gF('brand');
	if (!fieldToReset) return;
	fieldToReset.value = fieldToReset.getItemAt(0);;
}
function backToTopClicked() {
	resetBrandAndModelsFields();
	gotoPage(this, 0);
}
function isPageForAnnotations(pageIndex) {
	for (var i = 0; i < annotationPageIndices.length; ++i)
		if (annotationPageIndices[i] == pageIndex)
			return true;
	return false;
}
function setBackToTopFunctionality() {
	// negative end index?
	if (this.numPages - totalAppendedPdfPages < 0) return;
	var firstBackToTopPageIdx = annotationPageIndices && annotationPageIndices[0] != undefined ? annotationPageIndices[0] + 1 : 0;
	var lastBackToTopPageIdx = this.numPages - totalAppendedPdfPages;
	for (var pageIdx = firstBackToTopPageIdx; pageIdx < lastBackToTopPageIdx; ++pageIdx) {
		if (isPageForAnnotations(pageIdx)) continue;

		var pageLinks = getPageLinks(pageIdx);
		if (!pageLinks || pageLinks.length == 0) continue;

		var link = pageLinks[0];
		if (!link) continue;
		link.setAction('backToTopClicked()');
	}
}

//********************** Misc **********************/
function setupBrandAndModelListeners() {
	for (var k in fieldConfig)
		applyListener(k, fieldConfig[k].isSelect);
}

// ********************** "Main" **********************/
// OPT(2): open-time setup batched under Doc.delay — addAnnot creates the
// annotations already hidden and applyListener primes 8 field values (each
// still running its Calculate chain, exactly as before), but the appearance
// redraws all coalesce into the single repaint at delay=false. Doc.calculate
// is NOT suspended here: the open-time chain is what primes prevValues, and
// that behavior is preserved bit-for-bit.
batchedUpdate(function() {
	setupToggleableAnnotations();
	sortVisibilityAnnotations();
	setupDataFromServer();
	setupBrandAndModelListeners();
	// REOPEN(R4): the annotations hold whatever was saved (possibly by an
	// older, buggier script version) while the fields hold the user's saved
	// selections -- derive the visible set from the field values once, so
	// the document reopens exactly where it was left. Every write is
	// guarded, so a consistent save reopens with zero redraws.
	if (reopenedDocument) hideableControllerValueChanged();
}, false, false);

`;

export default scripts;
